import axios from 'axios';
import numeral from 'numeral';

import Config from '../utils/config.js';
import URL from '../utils/urls.js';
import Utils from '../utils/mci-utils.js';
import { addConsignmentLimits } from './apiService.js';

/**
 * Get premium rates on goods
 * @param {*} section
 * @param {*} category
 * @param {*} conveyance
 * @param {*} insurer_id
 */
const getGoodsRate = (section, category, conveyance, insurer_id) => {
  var AGENCY = [],
    params = {
      insurer: insurer_id,
      section: section,
      category: category,
      conveyance: conveyance
    };

  /* Check if skin is not a broker skin */
  if (!(AGENCY.length > 0)) {
    /* Check if Insurer is Sanlam and conveyance is air */
    if (
      insurer_id === 'd55890b5-6134-4b69-ba92-fa90a5f89fb1' &&
      conveyance === Config.CONVEYANCE.AIR
    ) {
      params.conveyance = Config.CONVEYANCE.SEA;
    }
  } else if (insurer_id.length > 0) {
    /* Check if Insurer is Sanlam and conveyance is air */

    if (
      insurer_id === 'd55890b5-6134-4b69-ba92-fa90a5f89fb1' &&
      conveyance === Config.CONVEYANCE.AIR
    ) {
      params.conveyance = Config.CONVEYANCE.SEA;
    }
  }

  return axios.get(URL.premiumRates, { params: params });
};

/**
 * Get Insurer Other Rates
 * @param {*} insurer_id
 */
const getOtherRates = insurer_id => {
  return axios.get(URL.otherRates, {
    params: { insurer: insurer_id }
  });
};

/**
 * Get Stamp Duty
 */
const getStampDuty = () => {
  return axios.get(URL.stampDuty);
};

/**
 * Get Comparison rates for goods
 * @param {*} is_containerized
 * @param {*} category
 * @param {*} conveyance
 */
const getComparisonRates = section => {
  return axios.get(URL.comparisonRates, {
    params: {
      section
    }
  });
};

const getInsurerValue = (insurer, conveyance, packaging, icc) => {
  var value = 0;
  var is_containerised = packaging === 'CONTAINERISED';
  if (icc === 'ICC-A') {
    if (conveyance === Config.CONVEYANCE.AIR) {
      value = insurer.icc_a.air_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && is_containerised) {
      value = insurer.icc_a.sea_containerised_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && !is_containerised) {
      value = insurer.icc_a.sea_non_containerised_value;
    }
  } else if (icc === 'ICC-B') {
    if (conveyance === Config.CONVEYANCE.AIR) {
      value = insurer.icc_b.air_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && is_containerised) {
      value = insurer.icc_b.sea_containerised_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && !is_containerised) {
      value = insurer.icc_b.sea_non_containerised_value;
    }
  } else if (icc === 'ICC-C') {
    if (conveyance === Config.CONVEYANCE.AIR) {
      value = insurer.icc_c.air_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && is_containerised) {
      value = insurer.icc_c.sea_containerised_value;
    } else if (conveyance === Config.CONVEYANCE.SEA && !is_containerised) {
      value = insurer.icc_c.sea_non_containerised_value;
    }
  }
  if (parseFloat(value) !== 0) return parseFloat(value);
};

// Revised Premium Calculation
const revisedPremiumCalculation = (
  insurer,
  goods,
  conveyance,
  packaging,
  transhipment,
  warehousing,
  currency_rate,
  other_rates,
  icc
) => {
  let TOTAL_CFR = Utils.sum(goods, 'cfr');
  let TOTAL_PREMIUM_ON_GOODS = 0;
  let TOTAL_PREMIUM = 0;
  let ITL = 0;
  let VAT = 0;

  // let PREMIUM_ON_GOODS = '';

  goods.map((good, index) => {
    let good_cfr = numeral(good.cfr).value();

    let premium_on_goods =
      (good_cfr * getInsurerValue(insurer, conveyance, packaging, icc)) / 100;

    return (TOTAL_PREMIUM_ON_GOODS += premium_on_goods);
  });

  VAT = TOTAL_PREMIUM_ON_GOODS * 0.18;

  ITL = (1.5 / 100 / (98.5 / 100)) * TOTAL_PREMIUM_ON_GOODS;

  TOTAL_PREMIUM = TOTAL_PREMIUM_ON_GOODS + VAT;
  return {
    gross_direct_premium: Math.round(TOTAL_PREMIUM_ON_GOODS * currency_rate),
    total_cfr: Math.round(TOTAL_CFR * currency_rate),
    vat: Math.round(VAT * currency_rate),
    itl: Math.round(ITL * currency_rate),
    premium: Math.round(TOTAL_PREMIUM * currency_rate)
  };
};

const calculatePremium = (
  insurer,
  goods,
  basis_of_valuation,
  conveyance,
  packaging,
  transshipment,
  warehousing,
  currency_rate,
  other_rates
) => {
  let insurer_other_rates = {};

  if (typeof other_rates !== 'undefined') {
    insurer_other_rates = Utils.filterOtherRatesByInsurer(
      other_rates,
      insurer.insurer
    );
  }

  if (insurer_other_rates != null) {
    let uplift_for_incidentals = 0;
    let insurable_value_of_goods = 0;

    let total_import_duty = 0;
    let total_excise_duty = 0;
    let TOTAL_VAT = 0;
    let TOTAL_CFR = 0;
    let TOTAL_PREMIUM_ON_GOODS = 0;

    goods.map((good, index) => {
      let valueOfGoods = parseFloat(good.cfr);

      let import_duty = valueOfGoods * parseFloat(good.import_duty);
      let excise_duty = valueOfGoods * parseFloat(good.excise_duty);
      let VAT = valueOfGoods * parseFloat(good.vat);

      total_import_duty += import_duty;
      total_excise_duty += excise_duty;
      TOTAL_VAT += parseFloat(VAT);

      // Fetch comparison rate for each insurer per good
      let premium =
        (valueOfGoods * getInsurerValue(insurer, conveyance, packaging)) / 100;

      TOTAL_CFR += parseFloat(valueOfGoods);
      return (TOTAL_PREMIUM_ON_GOODS += premium);
    });

    insurable_value_of_goods = TOTAL_CFR + uplift_for_incidentals;

    let import_declaration_form = 0;

    let TOTAL_TAXES = total_import_duty + total_excise_duty + TOTAL_VAT;
    let total_insurable_value = insurable_value_of_goods + TOTAL_TAXES;

    let other_rates_taxes = parseFloat(insurer_other_rates.taxes);
    let premium_on_taxes = TOTAL_TAXES * (other_rates_taxes / 100);

    // console.log("Other rates taxes: " + other_rates_taxes + " (" + other_rates_taxes + ")");
    // console.log("Premium on taxes: " + premium_on_taxes);
    // console.log("Premium on war-risk: " + premium_on_war_risk + " (" + insurer_other_rates.war_risk + ")");

    /* TRANSSHIPMENT */
    let premium_on_transshipment = 0;

    if (transshipment === true) {
      let transshipment_value = insurer_other_rates.transhipment / 100;
      premium_on_transshipment = total_insurable_value * transshipment_value;

      // console.log("Premium on Transhipment: " + premium_on_transshipment + " (" + transshipment_value + ")");
    }

    /* WAREHOUSING */
    let premium_on_warehousing = 0;

    if (warehousing === true) {
      let warehousing_value = insurer_other_rates.warehousing / 100;
      premium_on_warehousing = total_insurable_value * warehousing_value;

      // console.log("Premium on Warehousing: " + premium_on_warehousing + " (" + warehousing_value + ")");
    }

    let gross_direct_premium =
      TOTAL_PREMIUM_ON_GOODS +
      premium_on_warehousing +
      premium_on_transshipment;
    // console.log("Gross Direct Premium: " + gross_direct_premium);

    // TOTAL PREMIUM PAYABLE: ROUND OFF TO THE NEAREST SH 1
    let TOTAL_PAYABLE_PREMIUM = gross_direct_premium;

    // console.log("Insurance Training Levy: " + Utils.parseAmount(insurance_training_levy) + " (0.2)");
    // console.log("PHCF: " + policy_holder_compensation_fund);

    // console.log("Total Payble Premium (USD): " + TOTAL_PAYABLE_PREMIUM);
    // console.log("Total Payble Premium (KES): " + Utils.parseAmount(TOTAL_PAYABLE_PREMIUM * currency_rate));

    return {
      premium: Utils.parseAmount(TOTAL_PAYABLE_PREMIUM * currency_rate),
      premium_not_converted: Utils.parseAmount(TOTAL_PAYABLE_PREMIUM),
      total_sum_insured: insurable_value_of_goods,
      total_sum_insured_plus_taxes: total_insurable_value,
      gross_direct_premium: gross_direct_premium,
      premium_on_transhipment: premium_on_transshipment,
      premium_on_warehousing: premium_on_warehousing,
      premium_on_taxes: premium_on_taxes,
      idf: import_declaration_form,
      total_cfr: TOTAL_CFR
    };
  } else {
    return 0;
  }
};

export {
  calculatePremium,
  getGoodsRate,
  getOtherRates,
  getStampDuty,
  getComparisonRates,
  getInsurerValue,
  revisedPremiumCalculation
};



// WEBPACK FOOTER //
// ./src/utils/calculations.js